import {
  getJurisdictionLabel,
  isJurisdiction,
} from "./jurisdiction/structuredJurisdiction";

export type ManageFirmMainListJurisdictionOfficeInput = {
  countryName?: string;
  stateName?: string;
  /** Structured object or legacy label. */
  jurisdiction?: unknown;
  jurisdictionName?: string;
};

export type ManageFirmMainListJurisdictionDisplay = {
  label: string;
  indent: boolean;
};

const US_COUNTRY_LABELS = new Set([
  "usa",
  "u.s.a.",
  "us",
  "united states",
  "united states of america",
]);

const STATE_LEVEL_JURISDICTION_COUNTRIES = new Set([
  "United States of America",
  "Canada",
  "China",
]);

function isUsCountryLabel(value: string): boolean {
  return US_COUNTRY_LABELS.has(value.trim().toLowerCase());
}

function isStateLevelJurisdictionCountry(countryName: string): boolean {
  return STATE_LEVEL_JURISDICTION_COUNTRIES.has(countryName.trim());
}

function formatCountryStateLabel(countryName: string, stateName: string): string {
  const country = countryName.trim();
  const state = stateName.trim();
  if (country && state) {
    return `${country}, ${state}`;
  }
  if (country) {
    return country;
  }
  return state;
}

function formatManageFirmJurisdiction(
  office: ManageFirmMainListJurisdictionOfficeInput,
): string {
  return getJurisdictionLabel(office);
}

/** Resolve country/state from denormalized fields or `"State, Country"` labels. */
export function resolveManageFirmOfficeCountryAndState(
  office: ManageFirmMainListJurisdictionOfficeInput,
): { countryName: string; stateName: string } {
  const countryName = String(office.countryName ?? "").trim();
  const stateName = String(office.stateName ?? "").trim();
  if (countryName) {
    return { countryName, stateName };
  }
  if (isJurisdiction(office.jurisdiction)) {
    return {
      countryName: office.jurisdiction.countryName,
      stateName: office.jurisdiction.stateProvinceName ?? "",
    };
  }

  const label = formatManageFirmJurisdiction(office);
  if (!label) {
    return { countryName: "", stateName: "" };
  }

  const parts = label.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length !== 2) {
    return { countryName: label, stateName: "" };
  }

  const [first, second] = parts;
  if (
    isUsCountryLabel(second) ||
    second === "Canada" ||
    second === "China" ||
    second === "United States of America"
  ) {
    const resolvedCountry =
      isUsCountryLabel(second) || second === "United States of America"
        ? "United States of America"
        : second;
    return { countryName: resolvedCountry, stateName: first };
  }

  return { countryName: second, stateName: first };
}

/** Manage Firm main list jurisdiction column (Display_Firms-style). */
export function resolveManageFirmMainListJurisdictionDisplay(
  office: ManageFirmMainListJurisdictionOfficeInput,
): ManageFirmMainListJurisdictionDisplay {
  const { countryName, stateName } =
    resolveManageFirmOfficeCountryAndState(office);

  if (isStateLevelJurisdictionCountry(countryName)) {
    return {
      label: formatCountryStateLabel(countryName, stateName),
      indent: false,
    };
  }
  if (countryName === "British Virgin Islands") {
    return { label: "British Virgin Islands", indent: false };
  }
  if (countryName) {
    return { label: countryName, indent: false };
  }

  return {
    label: formatManageFirmJurisdiction(office),
    indent: false,
  };
}

export function formatManageFirmMainListJurisdiction(
  office: ManageFirmMainListJurisdictionOfficeInput,
): string {
  return resolveManageFirmMainListJurisdictionDisplay(office).label;
}

/** Legacy default sort: country, then state/province. */
export function resolveManageFirmMainListJurisdictionSortKey(
  office: ManageFirmMainListJurisdictionOfficeInput,
): string {
  const { countryName, stateName } =
    resolveManageFirmOfficeCountryAndState(office);
  return [countryName, stateName]
    .map((value) => value.trim())
    .filter(Boolean)
    .join("\u0001")
    .toLowerCase();
}
